/**
 * Iter 164 · RiskBadge and BulkActionBar helpers used across task views.
 */
import { AlertTriangle, Sparkles, X, Download, Pencil } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import BulkEditTasksDialog from "@/components/tasks/BulkEditTasksDialog";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const LEVEL_META = {
  high: { color: "#dc2626", bg: "bg-red-50", label: "HIGH" },
  med: { color: "#f59e0b", bg: "bg-amber-50", label: "MED" },
  low: { color: "#10b981", bg: "bg-emerald-50", label: "LOW" },
};

export function RiskBadge({ risk, size = "sm", showLabel = true }) {
  const [open, setOpen] = useState(false);
  if (!risk || risk.level === "low") return null;
  const meta = LEVEL_META[risk.level] || LEVEL_META.low;
  const reasons = risk.reasons || [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className={`inline-flex items-center gap-1 rounded-sm ${meta.bg} ${size === "xs" ? "px-1 py-0" : "px-1.5 py-0.5"} border`}
          style={{ borderColor: meta.color, color: meta.color }}
          data-testid={`risk-badge-${risk.level}`}
          title={reasons.map((r) => r.label).join(" · ")}
        >
          <AlertTriangle className={size === "xs" ? "h-2.5 w-2.5" : "h-3 w-3"} />
          {showLabel && <span className="font-mono font-bold text-[9px]">{meta.label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
            <div className="font-semibold text-sm">{meta.label} risk · score {risk.score}</div>
          </div>
          <div className="space-y-1">
            {reasons.map((r, i) => (
              <div key={i} className="text-xs flex items-start gap-1.5">
                <span className="text-muted-foreground">·</span>
                <span>{r.label}</span>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}


export function BulkActionBar({ wsId, selectedIds, onCleared, members = [],
                                  projects = [], onApplied, allTasks = [] }) {
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  // R159q · Feb 2026 — Gate every write action in this bar on the new
  // `task.bulk_edit` perm (or the pre-existing broad task.manage /
  // project.manage grants). Users without any of these keep the row
  // selection + Export CSV chip so they can still export, but the
  // dangerous / mutating buttons stay hidden.
  const { can, ready: permsReady } = useWorkspacePerms();
  const canBulkEdit = permsReady && (can("task.bulk_edit") || can("task.manage") || can("project.manage"));
  if (!selectedIds || selectedIds.length === 0) return null;

  const apply = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/tasks/bulk`, {
        task_ids: selectedIds, patch,
      });
      // Iter Feb 2026 R47 · Explain "0 updated" clearly using the new
      // matched/requested counters from the backend. Silent 0-modifieds
      // used to leave users guessing.
      const mod = data.modified ?? 0;
      const matched = data.matched ?? mod;
      const requested = data.requested ?? selectedIds.length;
      if (mod > 0) {
        toast.success(`Updated ${mod} task${mod === 1 ? "" : "s"}`);
      } else if (matched === 0) {
        toast.error(
          `No tasks updated — ${requested} selected but none matched this workspace. ` +
          `Refresh the page and try again.`,
        );
      } else {
        toast.message(`Already up-to-date (${matched} task${matched === 1 ? "" : "s"} had the same value)`);
      }
      if (onApplied) onApplied();
      if (onCleared) onCleared();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk update failed")); }
    finally { setBusy(false); }
  };

  const transferOrClone = async (mode, target_project_id) => {
    setBusy(true);
    try {
      const path = mode === "clone"
        ? `/workspaces/${wsId}/tasks/bulk-clone`
        : `/workspaces/${wsId}/tasks/bulk-transfer`;
      const { data } = await api.post(path, {
        task_ids: selectedIds, target_project_id,
      });
      const count = mode === "clone" ? data.cloned_count : data.transferred;
      toast.success(`${mode === "clone" ? "Cloned" : "Moved"} ${count} task${count === 1 ? "" : "s"}`);
      if (onApplied) onApplied();
      if (onCleared) onCleared();
    } catch (e) { toast.error(apiErrorMessage(e, `Bulk ${mode} failed`)); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    const wanted = new Set(selectedIds);
    const rows = allTasks.filter((t) => wanted.has(t.id));
    if (rows.length === 0) { toast.error("Nothing to export"); return; }
    const memberById = new Map(members.map((m) => [m.user_id, m]));
    const cols = ["title", "priority", "status", "assignee", "assignee_email",
                  "due_date", "start_date", "est_hours", "tags",
                  "project_name", "client_name", "description"];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(",")];
    rows.forEach((t) => {
      const m = memberById.get(t.assignee_id);
      lines.push([
        t.title, t.priority, t.status,
        m?.user?.name || "", m?.user?.email || "",
        t.due_date || "", t.start_date || "",
        t.est_hours ?? "", (t.tags || []).join(";"),
        t.project_name || "", t.client_name || "",
        (t.description || "").slice(0, 240),
      ].map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tasks-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} task(s) to CSV`);
  };

  // R89 · Bulk-inherit each selected task's assignee from its package's
  // skill_breakdown (matched by `assignee_hint == designation`). Only fills
  // in currently-unassigned tasks by default; the backend endpoint returns
  // a per-status breakdown so we can surface "skipped 3 already assigned".
  const inheritFromPackage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/tasks/inherit-assignees-from-package`, {
        task_ids: selectedIds,
      });
      const updated = data.updated || 0;
      const skips = [
        data.skipped_already_assigned ? `${data.skipped_already_assigned} already assigned` : null,
        data.skipped_no_hint ? `${data.skipped_no_hint} without designation hint` : null,
        data.skipped_no_match ? `${data.skipped_no_match} without a default assignee on the package` : null,
      ].filter(Boolean);
      if (updated > 0) {
        toast.success(
          `Inherited assignee on ${updated} task${updated === 1 ? "" : "s"}${skips.length ? " · " + skips.join(" · ") : ""}`,
        );
      } else {
        toast.message(`No tasks updated${skips.length ? " · " + skips.join(" · ") : ""}`);
      }
      if (onApplied) onApplied();
      if (onCleared) onCleared();
    } catch (e) { toast.error(apiErrorMessage(e, "Inherit-from-package failed")); }
    finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/tasks/bulk-delete`, {
        task_ids: selectedIds, patch: {},
      });
      const deleted = data.deleted ?? 0;
      const matched = data.matched ?? deleted;
      const requested = data.requested ?? selectedIds.length;
      if (deleted > 0) {
        toast.success(`Deleted ${deleted} task${deleted === 1 ? "" : "s"}`);
      } else if (matched === 0) {
        toast.error(
          `No tasks deleted — ${requested} selected but none matched this workspace. ` +
          `They may already be deleted or belong elsewhere. Refresh and try again.`,
        );
      } else {
        toast.message(`No changes — tasks may already be deleted`);
      }
      if (onApplied) onApplied();
      if (onCleared) onCleared();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk delete failed")); }
    finally { setBusy(false); setConfirmDelete(false); }
  };

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-foreground text-background rounded-full shadow-2xl px-4 py-2 flex items-center gap-2 border border-foreground/20"
         data-testid="bulk-action-bar">
      <span className="text-xs font-semibold px-2" data-testid="bulk-selected-count">
        {selectedIds.length} selected
      </span>
      <div className="w-px h-4 bg-background/30" />
      {canBulkEdit && (
      <>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost"
                   className="rounded-full h-8 text-xs text-background hover:bg-background/10"
                   data-testid="bulk-set-status">
            Set status
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-40 p-1">
          {["pending_approval", "open", "in_progress", "review", "blocked", "done"].map((s) => (
            <button key={s}
                     className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                     onClick={() => apply({ status: s })}
                     disabled={busy}
                     data-testid={`bulk-status-${s}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost"
                   className="rounded-full h-8 text-xs text-background hover:bg-background/10"
                   data-testid="bulk-set-priority">
            Set priority
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-32 p-1">
          {["low", "medium", "high", "urgent"].map((p) => (
            <button key={p}
                     className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                     onClick={() => apply({ priority: p })}
                     disabled={busy}
                     data-testid={`bulk-priority-${p}`}>
              {p}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost"
                   className="rounded-full h-8 text-xs text-background hover:bg-background/10"
                   data-testid="bulk-set-assignee">
            Assign to
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1 max-h-64 overflow-auto">
          <button
             className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded italic text-muted-foreground"
             onClick={() => apply({ assignee_id: null })}
             disabled={busy}
             data-testid="bulk-assignee-none">
            Unassigned
          </button>
          {members.map((m) => (
            <button key={m.user_id}
                     className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                     onClick={() => apply({ assignee_id: m.user_id })}
                     disabled={busy}
                     data-testid={`bulk-assignee-${m.user_id}`}>
              {m.user?.name || m.user?.email}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      </>
      )}
      <button type="button" onClick={exportCsv}
              className="text-xs px-2 py-1 border border-border/50 bg-background/10 hover:bg-background/20 text-background flex items-center gap-1"
              data-testid="bulk-export-csv">
        <Download className="w-3 h-3" /> Export CSV
      </button>
      {canBulkEdit && (
        <button
          type="button"
          onClick={() => setBulkEditOpen(true)}
          className="text-xs px-2 py-1 border border-border/50 bg-background/10 hover:bg-background/20 text-background flex items-center gap-1"
          data-testid="bulk-edit-open"
        >
          <Pencil className="w-3 h-3" /> Bulk edit
        </button>
      )}
      {canBulkEdit && (!confirmDelete ? (
        <Button size="sm" variant="ghost"
                 className="rounded-full h-8 text-xs text-red-300 hover:bg-red-500/20"
                 onClick={() => setConfirmDelete(true)}
                 data-testid="bulk-delete">
          Delete
        </Button>
      ) : (
        <Button size="sm"
                 className="rounded-full h-8 text-xs bg-red-500 hover:bg-red-600"
                 onClick={del} disabled={busy}
                 data-testid="bulk-delete-confirm">
          Confirm delete
        </Button>
      ))}
      {canBulkEdit && projects.length > 0 && (
        <>
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost"
                       className="rounded-full h-8 text-xs text-background hover:bg-background/10"
                       data-testid="bulk-move">
                Move to
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-1 max-h-64 overflow-auto">
              {projects.map((p) => (
                <button key={p.id}
                         className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                         onClick={() => transferOrClone("transfer", p.id)}
                         disabled={busy}
                         data-testid={`bulk-move-${p.id}`}>
                  {p.name}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost"
                       className="rounded-full h-8 text-xs text-background hover:bg-background/10"
                       data-testid="bulk-clone">
                Clone to
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-1 max-h-64 overflow-auto">
              <button
                 className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded italic"
                 onClick={() => transferOrClone("clone", null)}
                 disabled={busy}
                 data-testid="bulk-clone-same">
                Same project (make copies)
              </button>
              {projects.map((p) => (
                <button key={p.id}
                         className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted rounded"
                         onClick={() => transferOrClone("clone", p.id)}
                         disabled={busy}
                         data-testid={`bulk-clone-${p.id}`}>
                  {p.name}
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full h-8 text-xs text-background hover:bg-background/10"
            onClick={inheritFromPackage}
            disabled={busy}
            data-testid="bulk-inherit-from-package"
            title="For each selected task, pull the default assignee from its package skill breakdown (matched by designation)"
          >
            Inherit from package
          </Button>
        </>
      )}
      <button type="button" onClick={onCleared}
               className="ml-1 text-background/70 hover:text-background"
               data-testid="bulk-clear">
        <X className="h-4 w-4" />
      </button>

      {bulkEditOpen && (
        <BulkEditTasksDialog
          wsId={wsId}
          tasks={allTasks.filter((t) => selectedIds.includes(t.id))}
          projects={projects}
          members={members}
          memberName={(id) => {
            const m = members.find((x) => (x.user_id || x.id) === id);
            return m?.user?.name || m?.name || "";
          }}
          onClose={() => setBulkEditOpen(false)}
          onApplied={() => { setBulkEditOpen(false); if (onApplied) onApplied(); if (onCleared) onCleared(); }}
        />
      )}
    </div>
  );
}


export function AiNarrateButton({ wsId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/tasks/ai-risk-narrate?top_n=20`);
      toast.success(`AI narrated ${data.narratives_written} at-risk task(s)`);
      if (onDone) onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "AI narrate failed")); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={run} disabled={busy}
             className="h-8" data-testid="ai-narrate-btn">
      <Sparkles className="h-3.5 w-3.5 mr-1" />
      {busy ? "Narrating…" : "AI risk narratives"}
    </Button>
  );
}
